"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useEffect } from "react";
import mapboxgl, { LngLat, MapOptions } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { FeatureCollection, Geometry } from "geojson";
import { toaster } from "@/components/ui/toaster";
import { LayerToggleObjProps } from "./address-mapper";
import { Box } from "@chakra-ui/react";
const mapOptions: Omit<MapOptions, "container"> = {
  style: "mapbox://styles/mapbox/standard",
  zoom: 12.1,
  // Start with more zoomed-out view but not too far
  minZoom: 11,
  // Allow users to zoom out more
  maxZoom: 15,
  // Increase max zoom to allow closer inspection
  maxBounds: [[-122.6, 37.65],
  // Southwest coordinates
  [-122.25, 37.85] // Northeast coordinates
  ],
  dragRotate: false,
  // turn off rotation on drag
  touchPitch: false,
  // turn off pitch change w/touch
  touchZoomRotate: true,
  // turn on zoom/rotate w/touch
  config: {
    // Initial configuration for the Mapbox Standard style set above. By default, its ID is `basemap`.
    basemap: {
      // 'default', 'faded', or 'monochrome'
      theme: "monochrome"
    }
  }
};
interface MapProps {
  lon: number;
  lat: number;
  address: string | null;
  softStoryData: FeatureCollection<Geometry>;
  tsunamiData: FeatureCollection<Geometry>;
  liquefactionData: FeatureCollection<Geometry>;
  layerToggleObj: LayerToggleObjProps;
}
const addMarker = (center: LngLat, map: mapboxgl.Map) => {
  const el = document.createElement("div");
  return new mapboxgl.Marker({
    anchor: "bottom",
    element: el,
    className: "marker"
  }).setLngLat(center).addTo(map);
};
const Map: React.FC<MapProps> = t0 => {
  const $ = _c(14);
  const {
    lon,
    lat,
    address,
    softStoryData,
    tsunamiData,
    liquefactionData,
    layerToggleObj
  } = t0;
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const lastLon = useRef(lon);
  const lastLat = useRef(lat);
  let t1;
  let t2;
  if ($[0] !== address || $[1] !== lat || $[2] !== liquefactionData || $[3] !== lon || $[4] !== softStoryData || $[5] !== tsunamiData) {
    t1 = () => {
      const mapboxToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
      if (!mapContainerRef.current || !mapboxToken) {
        if (!toaster.isVisible("no-token")) {
          toaster.create({
            id: "no-token",
            description: "Mapbox access token or container is not set!",
            type: "error",
            duration: 5000,
            closable: true
          });
        }
        console.error("Mapbox access token or container is not set!");
        return;
      }
      mapboxgl.accessToken = mapboxToken;
      const center = lon && lat ? new LngLat(lon, lat) : null;
      if (!mapRef.current) {
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          ...(center ? {
            center
          } : {}),
          ...mapOptions
        });
        const map = mapRef.current;
        map.touchZoomRotate.disableRotation();
        const nav = new mapboxgl.NavigationControl({
          showCompass: false
        });
        map.addControl(nav, "bottom-right");
        if (center && address) {
          markerRef.current = addMarker(center, map);
        }
        map.on("load", () => {
          map.addSource("seismic", {
            type: "geojson",
            data: liquefactionData
          });
          map.addSource("tsunami", {
            type: "geojson",
            data: tsunamiData
          });
          map.addSource("soft-stories", {
            type: "geojson",
            data: softStoryData
          });
          map.addLayer({
            id: "tsunamiLayer",
            source: "tsunami",
            type: "fill",
            slot: "middle",
            paint: {
              "fill-color": "#63B3ED",
              "fill-opacity": 0.25
            }
          });
          map.addLayer({
            id: "seismicLayer",
            source: "seismic",
            type: "fill",
            slot: "middle",
            paint: {
              "fill-color": "#F6AD55",
              "fill-opacity": 0.5
            }
          });
          map.addLayer({
            id: "softStoriesLayer",
            source: "soft-stories",
            type: "circle",
            slot: "middle",
            paint: {
              "circle-radius": 4.5,
              "circle-stroke-width": 1,
              "circle-stroke-color": "#FFFFFF",
              "circle-color": "#A0AEC0"
            }
          });
          map.on("error", _temp);
        });
      } else {
        const map_0 = mapRef.current;
        if (center) {
          if (address) {
            if (!markerRef.current) {
              markerRef.current = addMarker(center, map_0);
            }
            markerRef.current.setLngLat(center);
          } else {
            if (markerRef.current) {
              markerRef.current?.remove();
              markerRef.current = null;
            }
          }
          if (map_0.getCenter().lng !== center.lng || map_0.getCenter().lat !== center.lat) {
            map_0.panTo(center, {
              duration: 750
            });
            lastLon.current = lon;
            lastLat.current = lat;
          }
        }
        return;
      }
    };
    t2 = [lon, lat, address, liquefactionData, softStoryData, tsunamiData];
    $[0] = address;
    $[1] = lat;
    $[2] = liquefactionData;
    $[3] = lon;
    $[4] = softStoryData;
    $[5] = tsunamiData;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== layerToggleObj.layerId || $[9] !== layerToggleObj.toggleState) {
    t3 = () => {
      const handleToggleLayers = () => {
        if (!mapRef.current) {
          return;
        }
        const map_1 = mapRef.current;
        const layerId = layerToggleObj.layerId;
        if (layerId && map_1.getLayer(layerId)) {
          const newVisibility = layerToggleObj.toggleState ? "visible" : "none";
          map_1.setLayoutProperty(layerId, "visibility", newVisibility);
        }
      };
      if (layerToggleObj.layerId != "") {
        handleToggleLayers();
      }
    };
    $[8] = layerToggleObj.layerId;
    $[9] = layerToggleObj.toggleState;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== layerToggleObj) {
    t4 = [layerToggleObj];
    $[11] = layerToggleObj;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Box ref={mapContainerRef} w="full" h="full" />;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export default Map;
function _temp(e) {
  if (e.error && e.error.message.includes("access token")) {
    if (!toaster.isVisible("invalid-token")) {
      toaster.create({
        id: "invalid-token",
        description: "Invalid Mapbox access token!",
        type: "error",
        duration: 5000,
        closable: true
      });
    }
    console.error("Invalid Mapbox token:", e.error);
  }
}